import { formatJobNumber } from '@fieldops/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { cn } from '../../lib/cn.js'
import { errorMessage, useTRPC, type RouterOutputs } from '../../lib/trpc.js'
import { EmailCompose, type ComposeContact, type ComposeDraft, type ComposeFile } from '../email/EmailCompose.js'
import { Icon } from '../Icon.js'
import { Spinner } from '../ui.js'
import { whenLabel } from './text.js'

export type ScheduledSendRow = RouterOutputs['compose']['scheduled'][number]

const FALLBACK_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Australia/Perth'

/** The account's clock, for saying when something goes. */
export function useAccountZone(): string {
  const trpc = useTRPC()
  const board = useQuery({ ...trpc.settings.board.queryOptions(), staleTime: 10 * 60_000, retry: false })
  return board.data?.timezone ?? FALLBACK_ZONE
}

function kindLabel(row: ScheduledSendRow) {
  if (row.kind === 'QUOTE') return `Quote ${row.quote?.number ?? ''}`.trim()
  if (row.kind === 'INVOICE') return `Invoice ${row.invoice?.number ?? ''}`.trim()
  return 'Email'
}

/**
 * A message waiting for its time, or one that did not go. Send now, Edit and
 * Cancel while it waits; a failure says why and offers Try again. Edit opens
 * the same compose window with the message as it was saved. Decision 95.
 */
export function ScheduledSendCard({
  row,
  timeZone,
  contacts = [],
  files = [],
  showJob = false,
}: {
  row: ScheduledSendRow
  timeZone: string
  contacts?: ComposeContact[]
  files?: ComposeFile[]
  /** In the Inbox, where the row is not already inside its job. */
  showJob?: boolean
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const refresh = () => {
    void queryClient.invalidateQueries(trpc.compose.pathFilter())
    void queryClient.invalidateQueries(trpc.inbox.pathFilter())
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
  }
  const sendNow = useMutation(trpc.compose.sendNow.mutationOptions({ onSuccess: refresh }))
  const cancel = useMutation(trpc.compose.cancelScheduled.mutationOptions({ onSuccess: refresh }))
  const sendQuote = useMutation(trpc.quotes.send.mutationOptions())
  const sendInvoice = useMutation(trpc.invoices.send.mutationOptions())

  const failed = row.status === 'FAILED'
  const going = row.status === 'SENDING' || sendNow.isPending
  const busy = going || cancel.isPending
  const document =
    row.kind === 'QUOTE' && row.quoteId
      ? { kind: 'quote' as const, id: row.quoteId }
      : row.kind === 'INVOICE' && row.invoiceId
        ? { kind: 'invoice' as const, id: row.invoiceId }
        : null

  const run = async (action: () => Promise<unknown>) => {
    setError(null)
    try {
      await action()
    } catch (failure) {
      setError(errorMessage(failure))
    }
  }

  const sendDocument = async (draft: ComposeDraft) => {
    if (!document) return
    const input = {
      id: document.id,
      to: draft.to[0] ?? null,
      message: draft.body === '' ? null : draft.body,
      ...(draft.subject ? { subject: draft.subject } : {}),
      ...(draft.cc.length > 0 ? { cc: draft.cc } : {}),
      ...(draft.bcc.length > 0 ? { bcc: draft.bcc } : {}),
      ...(draft.attachmentIds.length > 0 ? { attachmentIds: draft.attachmentIds } : {}),
    }
    if (document.kind === 'quote') await sendQuote.mutateAsync(input)
    else await sendInvoice.mutateAsync(input)
    refresh()
  }

  const to = row.toAddresses.length ? row.toAddresses.join(', ') : 'the client’s address'

  return (
    <div
      role="group"
      aria-label={failed ? 'Scheduled email that did not send' : 'Scheduled email'}
      className={cn(
        'flex w-full gap-3 rounded-xl bg-white p-3.5 ring-1 ring-inset',
        failed ? 'ring-red-200' : 'ring-violet-200',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'grid size-9 shrink-0 place-items-center rounded-lg',
          failed ? 'bg-red-50 text-red-600' : 'bg-violet-50 text-violet-600',
        )}
      >
        <Icon name={failed ? 'alert' : 'mail'} className="size-4" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span
            className={cn(
              'text-[10px] font-bold tracking-[0.08em] uppercase',
              failed ? 'text-red-600' : 'text-violet-700',
            )}
          >
            {failed ? 'Did not send' : going ? 'Sending now' : `${kindLabel(row)} · scheduled`}
          </span>
          <span className="text-[12px] text-ink-500">{whenLabel(row.sendAt, timeZone)}</span>
        </div>
        <p className="truncate text-[14px] font-semibold text-ink-900">{row.subject || kindLabel(row)}</p>
        <p className="truncate text-[12px] text-ink-500">
          To {to}
          {row.sender ? ` · by ${row.sender.name}` : ''}
          {showJob && row.job ? ` · Job ${formatJobNumber(row.job)} · ${row.job.client.name}` : ''}
        </p>
        {failed && row.failureReason ? <p className="text-[12px] text-red-700">{row.failureReason}</p> : null}
        {error ? (
          <p role="alert" className="text-[12px] text-red-700">
            {error}
          </p>
        ) : null}
        <div className="mt-1 flex items-center gap-3 text-[13px] font-medium">
          <button
            type="button"
            disabled={busy}
            className="flex items-center gap-1.5 text-brand-700 hover:underline disabled:opacity-50"
            onClick={() => void run(() => sendNow.mutateAsync({ id: row.id }))}
          >
            {sendNow.isPending ? <Spinner className="size-3.5" /> : null}
            {failed ? 'Try again' : 'Send now'}
          </button>
          <button
            type="button"
            disabled={busy}
            className="text-ink-700 hover:underline disabled:opacity-50"
            onClick={() => setEditing(true)}
          >
            Edit
          </button>
          <button
            type="button"
            disabled={busy}
            className="text-ink-500 hover:text-red-700 hover:underline disabled:opacity-50"
            onClick={() => void run(() => cancel.mutateAsync({ id: row.id }))}
          >
            Cancel
          </button>
        </div>
      </div>

      <EmailCompose
        open={editing}
        onClose={() => {
          setEditing(false)
          refresh()
        }}
        title={document ? (document.kind === 'quote' ? 'Send quote' : 'Email invoice') : 'Scheduled email'}
        sendLabel={document ? (document.kind === 'quote' ? 'Send quote' : 'Send invoice') : 'Send now'}
        contacts={contacts}
        files={files}
        jobId={row.jobId}
        clientId={row.clientId}
        replyToMessageId={row.replyToMessageId}
        document={document}
        scheduled={{ id: row.id, sendAt: row.sendAt }}
        allowBlankTo={document !== null}
        subjectOptional={document !== null}
        bodyOptional={document !== null}
        locked={document ? [{ name: `${kindLabel(row).split(' ').at(-1)}.pdf`, detail: 'Attached when it sends' }] : []}
        initial={{
          to: row.toAddresses,
          cc: row.ccAddresses,
          bcc: row.bccAddresses,
          subject: row.subject ?? '',
          body: row.body ?? '',
          attachmentIds: row.attachmentIds,
        }}
        {...(document ? { onSend: sendDocument } : {})}
      />
    </div>
  )
}
